/**
 * Strip the SSO callback's query out of the address bar BEFORE any analytics SDK
 * boots, and before any third-party container script can read
 * `window.location.href`.
 *
 * The backend's SSO redirect lands as
 * `/auth/callback?sso=success&code=<one-time-uuid>`, and that code exchanges for
 * a customer session (60s TTL, single use). Two sinks read the raw address bar:
 *   - `gtm.client.ts` calls `createGtm({ loadScript: true })`, which appends
 *     `gtm.js` immediately. Any All Pages / GA4 / HubSpot / LinkedIn tag inside
 *     that container reads `window.location.href` — third-party code we do not
 *     control and cannot redact after the fact.
 *   - `mixpanel.client.ts` initialises the SDK, which attaches `$current_url`
 *     from `window.location.href` to EVERY event, not just pageviews.
 *
 * Why this can't live in the page: `callback.vue` strips the code in
 * `onMounted`, which runs long after plugins initialise.
 *
 * The whole query goes, not a list of named params — nothing on this route is
 * worth keeping in the URL, and matching by name is the thing that silently
 * fails when a new param appears. The router's own sinks (`to.fullPath`) are not
 * covered by this: they are gated on the same route in `gtm.client.ts` and
 * `mixpanel-pageviews.client.ts`.
 *
 * Prefix `01-` so this runs after `00-sanitize-config` but before every
 * analytics plugin (Nuxt loads plugins in alphabetical order).
 *
 * Safe for the callback page: `callback.vue` reads the code from `route.query`,
 * which vue-router resolved at navigation time and holds independently of
 * `history`. Rewriting the address bar does not disturb it — the same reason the
 * page's own later `replaceState` works.
 */
export default defineNuxtPlugin(() => {
  if (!import.meta.client) return;

  if (window.location.pathname !== "/auth/callback" || !window.location.search) return;

  // Preserve history.state or vue-router loses scroll restoration and
  // back/forward linkage for this entry.
  window.history.replaceState(window.history.state, "", window.location.pathname);
});
